"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  getLoadedCatalog,
  loadLocaleCatalog,
  type LocaleCatalog,
} from "@/lib/i18n-catalogs";
import {
  createLocaleCookie,
  interpolate,
  localeStorageKey,
  resolveLocale,
  supportedLocales,
} from "@/lib/i18n-core.mjs";

export type Locale = (typeof supportedLocales)[number];
// A type-position import, so the key list is checked against the real catalog
// without the catalog reaching the bundle.
export type TranslationKey = keyof typeof import("@/locales/en.json").default;
type Parameters = Record<string, string | number>;
type Translator = (key: TranslationKey, parameters?: Parameters) => string;
type TextTranslator = (value: string) => string;

let indexedCatalog: LocaleCatalog | null = null;
let englishValueKeys = new Map<string, TranslationKey[]>();

// Keyed on the catalog's identity rather than built once at module scope,
// because English now arrives after this module has evaluated.
function englishKeysFor(english: LocaleCatalog, value: string) {
  if (indexedCatalog !== english) {
    indexedCatalog = english;
    englishValueKeys = new Map();
    for (const [key, text] of Object.entries(english) as [
      TranslationKey,
      string,
    ][]) {
      const keys = englishValueKeys.get(text) ?? [];
      keys.push(key);
      englishValueKeys.set(text, keys);
    }
  }
  return englishValueKeys.get(value);
}

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translator;
  translateText: TextTranslator;
  formatDate: (
    value: Date | string | number,
    options?: Intl.DateTimeFormatOptions,
  ) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

// main.tsx resolves the visitor's locale with this before it loads a catalog,
// so the provider has to reach the same answer or it would paint one locale
// and immediately swap to another. An explicit browser preference wins, but a
// first-time visitor starts in English regardless of the browser's UI locale.
export function preferredLocale(): Locale {
  const cookieLocale = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith("autar_locale="))
    ?.split("=")[1];
  return resolveLocale(
    window.localStorage.getItem(localeStorageKey) ?? cookieLocale,
    null,
  );
}

export function LocalizationProvider({
  children,
  initialLocale = preferredLocale(),
  initialCatalog = getLoadedCatalog(initialLocale),
}: {
  children: ReactNode;
  initialLocale?: Locale;
  initialCatalog?: LocaleCatalog;
}) {
  const [locale, updateLocale] = useState<Locale>(initialLocale);
  const [catalog, updateCatalog] = useState<LocaleCatalog>(initialCatalog);
  const [fallback, updateFallback] = useState<LocaleCatalog>(() =>
    getLoadedCatalog("en"),
  );

  const applyLocale = useCallback(async (nextLocale: Locale) => {
    const nextCatalog = await loadLocaleCatalog(nextLocale);
    updateCatalog(nextCatalog);
    updateLocale(nextLocale);
  }, []);

  useEffect(() => {
    const resolvedLocale = preferredLocale();
    if (resolvedLocale !== initialLocale)
      void applyLocale(resolvedLocale).catch(() => undefined);
  }, [applyLocale, initialLocale]);

  // English only backs the lookups below — it is never what this locale
  // paints with — so it is fetched after the first render rather than ahead of
  // it. The load is cached, so an English visitor pays nothing for this.
  useEffect(() => {
    void loadLocaleCatalog("en")
      .then(updateFallback)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback(
    (nextLocale: Locale) => {
      window.localStorage.setItem(localeStorageKey, nextLocale);
      document.cookie = createLocaleCookie(nextLocale);
      // A failed load leaves the current locale showing; the user can retry.
      void applyLocale(nextLocale).catch(() => undefined);
    },
    [applyLocale],
  );

  const value = useMemo<I18nContextValue>(() => {
    const t: Translator = (key, parameters) =>
      interpolate(catalog[key] ?? fallback[key] ?? key, parameters);
    return {
      locale,
      setLocale,
      t,
      translateText: (text) => {
        const keys = englishKeysFor(fallback, text);
        if (!keys) return text;
        const translations = new Set(keys.map((key) => t(key)));
        return translations.size === 1
          ? (translations.values().next().value ?? text)
          : text;
      },
      formatDate: (value, options) =>
        new Intl.DateTimeFormat(locale, options).format(new Date(value)),
      formatNumber: (value, options) =>
        new Intl.NumberFormat(locale, options).format(value),
    };
  }, [catalog, fallback, locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context)
    throw new Error("useI18n must be used inside LocalizationProvider");
  return context;
}

const navigationKeys: Record<string, TranslationKey> = {
  Fleet: "nav.section.fleet",
  Standards: "nav.section.standards",
  Analytics: "nav.section.analytics",
  Agents: "nav.section.agents",
  "Autar Docs": "nav.section.docs",
  Settings: "nav.section.settings",
  Home: "nav.accountHome",
  Repositories: "nav.repositories",
  Inspect: "nav.inspect",
  "Upload Archive": "nav.uploadArchive",
  "Fleet Agents": "nav.fleetAgents",
  "Estate Setup": "nav.estateSetup",
  Access: "nav.access",
  Providers: "nav.providers",
  Subscriptions: "nav.subscriptions",
  "User Settings": "nav.userSettings",
  "CLI Guide": "nav.cliGuide",
  "Admin Dashboard": "nav.adminDashboard",
  "Feedback Analysis": "nav.feedbackAnalysis",
  "Preference Engine": "nav.preferenceEngine",
  Suppressed: "nav.suppressed",
  "Dependency Policy": "nav.dependencyPolicy",
  "Agent Health": "nav.agentHealth",
  "Agent Standards": "nav.agentsMdBuilder",
  "Engineering Waste Index": "nav.engineeringWaste",
  "SDLC Findings": "nav.sdlcFindings",
  "Dependency Tracker": "nav.dependencyTracker",
  "Value Stream Waste": "nav.valueStreamWaste",
  "Engineering Waste": "nav.section.engineeringWaste",
  Automations: "nav.section.automations",
  "Estate Intelligence": "nav.section.issueIntelligence",
  "Estate Settings": "nav.section.estateSettings",
  "Standards and Tastes": "nav.section.standardsAndTastes",
  Overview: "estates.estate.nav.overview",
  "Waste Index": "repositoryOverview.wasteIndex",
  "Impact and Outcomes": "nav.impactOutcomes",
  "90-Day Plan": "common.ninetyDayPlan",
  "Maintenance Plan": "nav.maintenancePlan",
  Campaigns: "nav.campaigns",
  Dependencies: "shell.dependencies",
  "Commit Intelligence": "shell.commit.intelligence",
  "Repo Inspection": "nav.repoInspection",
  Public: "repositoryFinder.public",
  Private: "nav.private",
  "Coming Soon": "estates.estate.nav.comingSoon",
  "Demo Data": "app.demoData",
  Alpha: "nav.status.alpha",
  Beta: "estates.estate.nav.beta",
};

export function translateNavigationLabel(label: string, t: Translator) {
  const key = navigationKeys[label];
  return key ? t(key) : label;
}
